import { ReactNode, useState } from "react";
import Image from "next/image";
import { useTranslation } from "next-i18next";

import { useCheckIsMobile } from "@sellernote/shared/src/utils/hook";
import useOnceActivatedIntersectionObserver from "@sellernote/features/src/common/hooks/useOnceActivatedIntersectionObserver";
import useCheckIsFulfillmentPage from "@sellernote/shipda-kr/src/hooks/common/useCheckIsFulfillmentPage";
import Badge from "@sellernote/ui/src/sds-v2/components/Badge";
import ChevronLeftIcon from "@sellernote/ui/src/sds-v2/components/svgIcons/FourChevronLeftIcon";
import ChevronRightIcon from "@sellernote/ui/src/sds-v2/components/svgIcons/FourChevronRightIcon";
import { COLOR, TEXT_COLOR } from "@sellernote/ui/src/sds-v2/styles/colors";

import CommonStyled from "../index.styles";
import Styled from "./index.styles";

export type SliderItem = {
  title: ReactNode;
  desc: ReactNode;
  badgeType?: string;
  imageUrl: string;
  mobileImageUrl: string;
};

export default function LandingSectionSlider({
  sectionBackgroundColor = COLOR.bgColor_1,
  sliderBackgroundColor = COLOR.wh,
  title,
  sliderList,
}: {
  sectionBackgroundColor?: string;
  sliderBackgroundColor?: string;
  title: ReactNode;
  sliderList: SliderItem[];
}) {
  const { isFulfillmentPage } = useCheckIsFulfillmentPage();

  const { isMobile } = useCheckIsMobile();

  const { t } = useTranslation(["main-landing"]);

  const [page, setPage] = useState(1);

  const currentPageContents = sliderList[page - 1];

  const lastPage = sliderList.length;

  const handlePrevMove = () => {
    if (page <= 1) return setPage(lastPage);

    setPage(page - 1);
  };

  const handleNextMove = () => {
    if (page >= lastPage) return setPage(1);

    setPage(page + 1);
  };

  const { target, isActivatedTarget } = useOnceActivatedIntersectionObserver({
    threshold: 0.3,
  });

  return (
    <Styled.container
      ref={target}
      sectionBackgroundColor={sectionBackgroundColor}
    >
      <CommonStyled.sectionTitle
        className={`fade-up-animation ${isActivatedTarget ? "active" : ""}`}
      >
        {title}
      </CommonStyled.sectionTitle>

      <Styled.sliderList
        className={`fade-up-animation ${isActivatedTarget ? "active" : ""}`}
        sliderBackgroundColor={sliderBackgroundColor}
      >
        <Styled.slider>
          <Image
            src={
              isMobile
                ? currentPageContents.mobileImageUrl
                : currentPageContents.imageUrl
            }
            alt={isFulfillmentPage ? "Company Logo" : "Interview Member Photo"}
            width={isMobile ? 640 : 320}
            height={isMobile ? 240 : 220}
          />

          <Styled.contents
            className={`fade-up-animation ${isActivatedTarget ? "active" : ""}`}
          >
            <Styled.contentsHeader>
              {currentPageContents?.title && (
                <span className="title">{currentPageContents.title}</span>
              )}

              {currentPageContents?.badgeType && (
                <Badge
                  borderType="stroked"
                  label={
                    currentPageContents.badgeType === "forwarding"
                      ? t("main-landing:쉽다가일하는방법섹션_뱃지_포워딩")
                      : t("main-landing:쉽다가일하는방법섹션_뱃지_풀필먼트")
                  }
                  colorInfo={{
                    background: COLOR.secondary_50,
                    text: COLOR.secondary_400,
                    border: COLOR.secondary_400,
                  }}
                />
              )}
            </Styled.contentsHeader>

            {currentPageContents?.desc && (
              <p className="desc">{currentPageContents.desc}</p>
            )}

            <Styled.pagination>
              <ChevronLeftIcon
                width={24}
                height={24}
                color={TEXT_COLOR.black_3}
                onClick={handlePrevMove}
              />

              <div>
                {page} / {sliderList.length}
              </div>

              <ChevronRightIcon
                width={24}
                height={24}
                color={TEXT_COLOR.black_3}
                onClick={handleNextMove}
              />
            </Styled.pagination>
          </Styled.contents>
        </Styled.slider>
      </Styled.sliderList>
    </Styled.container>
  );
}
